import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType, concatLatestFrom } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, groupBy, EMPTY } from 'rxjs';
import { map, switchMap, exhaustMap, mergeMap, catchError, filter, tap } from 'rxjs/operators';
import { NGXLogger } from 'ngx-logger';
import { MatomoTracker } from '@ngx-matomo/tracker';
import { MatSnackBar } from '@angular/material/snack-bar';
import { mappedHttpErrorResponseOperator } from '@adnova/jf-ng-components';

import { AnnouncementService, ProblemDTO } from '../../openapi/shell-service-openapi';
import { UserAnnouncementEntitiesActions } from './user-announcement-entities.actions';
import { UserAnnouncementEntitiesSelectors } from './user-announcement-entities.selectors';
import {
  InfoPopupDialogActions,
  InfoPopupDialogSelectors,
} from '../../modules/dialogs/info-popup-dialog';

/**
 * Effects für die Endnutzer-Announcements.
 *
 * Handhabt das Laden der aktuellen Announcements, das Markieren als gelesen,
 * das Laden von Bildern und die Dialog-Steuerung.
 */
@Injectable()
export class UserAnnouncementEntitiesEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly service = inject(AnnouncementService);
  private readonly logger = inject(NGXLogger);
  private readonly tracker = inject(MatomoTracker);
  private readonly snackBar = inject(MatSnackBar);

  /**
   * Lädt die aktuell aktiven, ungelesenen Announcements.
   *
   * Prüft vorher `isLoaded` — nur laden, wenn noch nicht geladen (einmal pro Session).
   * Liest zuerst die Settings: nur bei `enabled: true` werden Announcements geladen.
   * Verwendet `switchMap` (Lese-Operation).
   */
  readonly loadCurrent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAnnouncementEntitiesActions.loadCurrent),
      concatLatestFrom(() => this.store.select(UserAnnouncementEntitiesSelectors.isLoaded)),
      filter(([_, isLoaded]) => !isLoaded),
      switchMap(() =>
        this.service.readAnnouncementSettings().pipe(
          switchMap((settings) => {
            this.store.dispatch(UserAnnouncementEntitiesActions.loadSettingsSuccess({ enabled: settings.enabled }));
            if (!settings.enabled) {
              // INFO: Pop-Ups deaktiviert → nur leichtgewichtigen Count laden für Glocken-Icon.
              // Keine Announcement-Objekte nötig, da kein Dialog geöffnet wird.
              return this.service.readUnreadAnnouncementCount().pipe(
                map((response) =>
                  UserAnnouncementEntitiesActions.loadCurrentSuccess({ announcements: [], unreadCount: response.count }),
                ),
              );
            }
            return this.service.readCurrentAnnouncements().pipe(
              map((announcements) =>
                UserAnnouncementEntitiesActions.loadCurrentSuccess({ announcements }),
              ),
            );
          }),
          catchError((error) => of(error).pipe(
            mappedHttpErrorResponseOperator<ProblemDTO>(error),
            map((mappedError) => {
              this.logger.error('Fehler beim Laden der aktuellen Announcements.', mappedError);
              return UserAnnouncementEntitiesActions.loadCurrentFailure({ error: mappedError });
            }),
          )),
        ),
      ),
    ),
  );

  /**
   * Aktualisiert die Announcement-Settings (aktivieren/deaktivieren).
   *
   * Verwendet `exhaustMap` (globale Mutation, Doppelklick-Schutz).
   * Bei Fehler wird der vorherige `enabled`-Wert wiederhergestellt.
   */
  readonly updateSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAnnouncementEntitiesActions.updateSettings),
      exhaustMap(({ enabled }) => {
        /*
         * INFO: previousEnabled wird als !enabled berechnet, nicht über concatLatestFrom.
         * Grund: Der optimistische Reducer on(updateSettings) setzt enabled BEVOR der
         * Effect läuft. concatLatestFrom würde den bereits aktualisierten Wert lesen,
         * sodass der Rollback bei Failure wirkungslos wäre.
         */
        const previousEnabled = !enabled;
        return this.service.updateAnnouncementSettings({ enabled }).pipe(
          map((settings) =>
            UserAnnouncementEntitiesActions.updateSettingsSuccess({ enabled: settings.enabled }),
          ),
          catchError((error) => of(error).pipe(
            mappedHttpErrorResponseOperator<ProblemDTO>(error),
            map((mappedError) => {
              this.logger.error('Fehler beim Aktualisieren der Announcement-Settings.', mappedError);
              return UserAnnouncementEntitiesActions.updateSettingsFailure({
                previousEnabled,
                error: mappedError,
              });
            }),
          )),
        );
      }),
    ),
  );

  /**
   * Markiert den verlassenen Artikel als gelesen bei Navigation (Weiter/Zurück).
   * Die `currentId` wird von der Komponente mitgegeben (vor der Reducer-Ausführung ermittelt).
   */
  readonly markReadOnNavigate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAnnouncementEntitiesActions.navigate),
      filter(({ currentId }) => !!currentId),
      map(({ currentId }) => UserAnnouncementEntitiesActions.markRead({ id: currentId! })),
    ),
  );

  /**
   * Markiert ein Announcement als gelesen.
   *
   * Verwendet `groupBy` + `mergeMap` + `exhaustMap` (ID-spezifische Mutation):
   * Doppelklick-Schutz pro Announcement, parallele Requests für verschiedene IDs.
   */
  readonly markRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAnnouncementEntitiesActions.markRead),
      groupBy(action => action.id),
      mergeMap(group$ => group$.pipe(
        exhaustMap(({ id }) =>
          this.service.markAnnouncementAsRead(id).pipe(
            map(() => UserAnnouncementEntitiesActions.markReadSuccess({ id })),
            catchError((error) => of(error).pipe(
              mappedHttpErrorResponseOperator<ProblemDTO>(error),
              map((mappedError) => {
                this.logger.error('Fehler beim Markieren als gelesen.', mappedError);
                return UserAnnouncementEntitiesActions.markReadFailure({ id, error: mappedError });
              }),
            )),
          ),
        ),
      )),
    ),
  );

  /**
   * Lädt ein Announcement-Bild und erzeugt eine Blob-URL.
   *
   * Verwendet `groupBy` + `mergeMap` + `exhaustMap` (pro imageId):
   * Verhindert doppeltes Laden desselben Bildes, erlaubt paralleles Laden verschiedener Bilder.
   */
  readonly loadImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAnnouncementEntitiesActions.loadImage),
      groupBy(action => action.imageId),
      mergeMap(group$ => group$.pipe(
        exhaustMap(({ imageId }) =>
          this.service.readAnnouncementImage(imageId).pipe(
            map((blob) =>
              UserAnnouncementEntitiesActions.loadImageSuccess({
                imageId,
                blobUrl: URL.createObjectURL(blob),
              }),
            ),
            catchError((error) => of(error).pipe(
              mappedHttpErrorResponseOperator<ProblemDTO>(error),
              map((mappedError) => {
                this.logger.error('Fehler beim Laden des Announcement-Bildes.', mappedError);
                return UserAnnouncementEntitiesActions.loadImageFailure({ imageId, error: mappedError });
              }),
            )),
          ),
        ),
      )),
    ),
  );

  /**
   * Lädt Bilder für alle Announcements nach erfolgreichem Laden.
   * Dispatcht `loadImage` für jedes Announcement mit einer `imageId`.
   */
  readonly loadImagesOnSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAnnouncementEntitiesActions.loadCurrentSuccess),
      switchMap(({ announcements }) => {
        const imageActions = announcements
          .filter((a) => !!a.imageId)
          .map((a) => UserAnnouncementEntitiesActions.loadImage({ imageId: a.imageId! }));
        return imageActions.length > 0 ? imageActions : EMPTY;
      }),
    ),
  );

  /**
   * Öffnet den Dialog im User-Modus, wenn Announcements vorhanden sind.
   */
  // INFO: Dialog nur öffnen wenn Pop-Ups aktiviert sind UND Announcements vorhanden.
  // Bei deaktivierten Pop-Ups wird nur der Count geladen (kein Dialog, announcements ist leer).
  // Der enabled-Check ist ein zusätzlicher Guard.
  readonly openDialogOnSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAnnouncementEntitiesActions.loadCurrentSuccess),
      concatLatestFrom(() => this.store.select(UserAnnouncementEntitiesSelectors.enabled)),
      filter(([{ announcements }, enabled]) => enabled && announcements.length > 0),
      map(() => InfoPopupDialogActions.openUserPopup()),
    ),
  );

  /**
   * Reagiert auf den X-Button-Klick im Info-Popup-Dialog.
   * Markiert das aktuelle Announcement als gelesen (im User-Modus) und schließt den Dialog.
   */
  readonly closeViaButton$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InfoPopupDialogActions.closeViaButton),
      concatLatestFrom(() => [
        this.store.select(InfoPopupDialogSelectors.mode),
        this.store.select(UserAnnouncementEntitiesSelectors.currentAnnouncement),
      ]),
      switchMap(([_, mode, announcement]) => {
        const actions: ReturnType<typeof InfoPopupDialogActions.close | typeof UserAnnouncementEntitiesActions.markRead>[] = [];
        if (mode === 'user') {
          this.tracker.trackEvent('info-popup', 'close');
          if (announcement?.id) {
            actions.push(UserAnnouncementEntitiesActions.markRead({ id: announcement.id }));
          }
        }
        actions.push(InfoPopupDialogActions.close());
        return actions;
      }),
    ),
  );

  /**
   * Markiert alle Announcements als gelesen (beim Öffnen der Archiv-Seite).
   *
   * Verwendet `exhaustMap` (globale Mutation, Doppelklick-Schutz).
   */
  readonly markAllRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAnnouncementEntitiesActions.markAllRead),
      exhaustMap(() =>
        this.service.markAllAnnouncementsAsRead().pipe(
          map(() => UserAnnouncementEntitiesActions.markAllReadSuccess()),
          catchError((error) => of(error).pipe(
            mappedHttpErrorResponseOperator<ProblemDTO>(error),
            map((mappedError) => {
              this.logger.error('Fehler beim Markieren aller Announcements als gelesen.', mappedError);
              return UserAnnouncementEntitiesActions.markAllReadFailure({ error: mappedError });
            }),
          )),
        ),
      ),
    ),
  );

  /**
   * Zeigt eine Fehlermeldung, wenn die Einstellungen nicht gespeichert werden konnten.
   * Nur aktive User-Interaktionen (Checkbox) bekommen eine Snackbar.
   */
  readonly updateSettingsFailureNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAnnouncementEntitiesActions.updateSettingsFailure),
      tap(() => this.snackBar.open(
        'Einstellung konnte nicht gespeichert werden. Bitte probiere es später erneut.',
        undefined,
        { duration: 5000 },
      )),
    ), { dispatch: false },
  );


  /**
   * Räumt Blob-URLs auf und setzt den State zurück, wenn der Dialog im User-Modus geschlossen wird.
   */
  readonly cleanupOnClose$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InfoPopupDialogActions.close),
      concatLatestFrom(() => [
        this.store.select(UserAnnouncementEntitiesSelectors.announcements),
        this.store.select(UserAnnouncementEntitiesSelectors.imageUrls),
      ]),
      filter(([_, announcements]) => announcements.length > 0),
      tap(([_, __, imageUrls]) => {
        Object.values(imageUrls).forEach((url) => URL.revokeObjectURL(url));
      }),
      map(() => UserAnnouncementEntitiesActions.reset()),
    ),
  );
}
